<script setup lang="ts">
import { RBN_CAMPSITE_DETAIL } from '@/utils/constants';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFavoriteTileProps {
  favorite: any;
  number: number;
  timeoutActive?: boolean;
}

const props = withDefaults(defineProps<CiFavoriteTileProps>(), {
  timeoutActive: false,
});

const emit = defineEmits<{
  'ci-favorite-item-remove-favorite': [value: { slug: string; index: number }];
  'ci-favorite-item-undo-remove-favorite': [value: { slug: string }];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const { $gettext } = useGettext();
const { comparePrice, favoriteIcon, markForRemoval, markedForRemoval, setHoverState, undoIcon, undoMarkForRemoval, onFavoriteClicked }
  = useFavoriteItem({
    favorite: props.favorite,
    index: props.number,
    timeoutActive: props.timeoutActive,
  });

/********************
 * FUNCTIONS         *
 ********************/
function onMarkForRemoval() {
  emit('ci-favorite-item-remove-favorite', { slug: props.favorite.slug, index: props.number });
  markForRemoval();
}

function onUndoMarkForRemoval() {
  emit('ci-favorite-item-undo-remove-favorite', { slug: props.favorite.slug });
  undoMarkForRemoval();
}
</script>

<template>
  <template v-if="!markedForRemoval && !props.timeoutActive">
    <nuxt-link
      :to="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: props.favorite.slug } })"
      class="grid grid-cols-3 gap-x-[30px] py-4 pl-2 pr-6 text-black hover:no-underline md:pr-2"
      :class="{ 'py-4': !props.favorite.is_active, 'pointer-events-none': !favorite.is_active }"
      data-testid="ci-favorite-list-item"
      @click="onFavoriteClicked(props.number)"
    >
      <div class="col-span-2 grid-cols-12 gap-x-[30px] md:col-span-3 md:grid md:items-center">
        <div
          class="col-span-12 truncate align-middle md:col-span-3"
          :data-testid="`ci-favorite-list-item--slug-${favorite.slug}`"
        >
          <span class="text-primary">{{ props.number }}.</span>
          <span
            class="ml-2 font-bold md:font-normal"
            :class="{ 'opacity-40': !favorite.is_active }"
            :title="props.favorite.name"
          >{{ props.favorite.name }}</span>
        </div>
        <div
          class="col-span-12 truncate text-gray md:col-span-2 md:text-center md:text-black"
          :class="{ 'opacity-40': !favorite.is_active }"
        >
          <span
            v-if="props.favorite.country"
            :title="props.favorite.country.name"
          >{{ props.favorite.country.name }}</span>
        </div>
        <div
          class="col-span-2 hidden truncate text-center md:block"
          :class="{ 'opacity-40': !favorite.is_active }"
        >
          <span
            v-if="props.favorite.federal_state"
            :title="props.favorite.federal_state.name"
          >{{
            props.favorite.federal_state.name
          }}</span>
        </div>
        <div
          v-if="props.favorite.is_active"
          class="col-span-2 hidden justify-center md:block"
        >
          <CiRatingRadial
            :rating="props.favorite.rating_avg_overall"
            class="m-auto w-12 justify-center"
            font-class="font-bold"
          />
        </div>
        <div
          v-if="props.favorite.is_active"
          class="col-span-12 text-info md:col-span-2 md:text-center"
        >
          <div
            v-if="comparePrice"
            class="flex md:justify-center"
          >
            <span class="mr-1">{{ $gettext('From') }}</span>
            <span>{{ parseFloat(comparePrice).toFixed(2) }}</span>
            <span class="ml-1">€</span>
          </div>
          <div v-else>
            -
          </div>
        </div>
        <div
          v-if="!props.favorite.is_active"
          class="col-span-12 md:col-span-4 md:text-center"
        >
          <span class="mb-0 mr-4 py-1 font-bold text-black">{{ $gettext('Campsite not available') }}</span>
        </div>
        <div
          v-if="props.favorite.is_active"
          class="col-span-1 hidden md:block"
        >
          <div class="flex h-full items-center">
            <CiAwesomeIcon
              :icon="favoriteIcon"
              class="icon ml-auto fill-danger"
              data-testid="ci-favorite-list-item--icon"
              ratio="0.8"
              @click.prevent.stop="onMarkForRemoval()"
              @mouseleave="setHoverState(false)"
              @mouseover="setHoverState(true)"
            />
          </div>
        </div>
      </div>
      <div class="col-span-1 flex flex-row items-center gap-x-5 md:hidden">
        <CiRatingRadial
          v-if="props.favorite.is_active"
          :rating="props.favorite.rating_avg_overall"
          :circle-class="'circle--primary'"
          class="w-full max-w-14 justify-center"
          font-class="font-bold"
        />
        <div
          v-if="props.favorite.is_active"
          class="flex h-full w-1/2 items-center"
        >
          <CiAwesomeIcon
            :icon="favoriteIcon"
            class="icon ml-auto fill-danger"
            ratio="0.8"
            @click.prevent.stop="markForRemoval()"
            @mouseleave="setHoverState(false)"
            @mouseover="setHoverState(true)"
          />
        </div>
      </div>
    </nuxt-link>
    <CiAwesomeIcon
      v-if="!props.favorite.is_active"
      :icon="favoriteIcon"
      class="icon absolute mr-6 fill-danger md:mr-2"
      :class="{ 'right-0 top-1/2 -translate-y-1/2 cursor-pointer': !favorite.is_active }"
      ratio="0.8"
      @mouseover="setHoverState(true)"
      @mouseleave="setHoverState(false)"
      @click="markForRemoval()"
    />
  </template>
  <div v-else class="m-auto flex items-center justify-between px-2 py-6">
    <div class="my-2 justify-start md:hidden">
      {{ $gettext('Campsite removed!') }}
    </div>
    <div class="hidden shrink grow basis-auto text-center md:block">
      {{ $gettext('Campsite removed from favorites!') }}
    </div>
    <CiButton
      tag="button"
      variant="text"
      class="font-bold text-black"
      @click="onUndoMarkForRemoval()"
    >
      <CiAwesomeIcon
        :icon="undoIcon"
        class="mr-2 fill-black"
        ratio="0.9"
      />
      <span class="md:hidden">{{ $gettext('Undo') }}</span>
      <span class="hidden md:block">{{ $gettext('Undo action') }}</span>
    </CiButton>
  </div>
</template>
